import { Fragment } from 'react'
import { Checkbox } from '@cerifi/ui/components/checkbox'
import { Currency } from '@cerifi/ui/components/currency'
import Guarantee from '@cerifi/ui/components/icons/guarantee'
import GuaranteeGray from '@cerifi/ui/components/icons/guarantee-gray'
import PaperAirplane from '@cerifi/ui/components/icons/paper-airplane'

interface EnrollmentMainCardProps {
  /** Name of the main product */
  title: React.ReactNode
  /** Gray text that appears at the top of the card */
  tag: string
  /** List of tags at the top of the card separated by bullet points */
  innerTags?: string[]
  /** whether or not the Dalton Review Plus checkbox is selected */
  checkedPro?: boolean
  /** Fires when the user checks or unchecks the Dalton Review Plus checkbox */
  checkedProChange?: (val: boolean) => void
  /** In dollars of the main product */
  price: number
  text?: string
  /** Adds guaranteed to pass banner */
  guaranteeToPass?: boolean
  /** Shows the dalton review checkbox */
  showDaltonReview?: boolean
  /** The price of the Dalton Review Plus */
  plusPrice?: number
  /** The price of the standalone Dalton Review Plus to show how much it is discounted */
  originalPlusPrice?: number
  /** Price of just the normal Dalton Review  (only use if showDaltonReview is checked) */
  reviewPrice?: number
  /** Price of the standalone Dalton Review to show how much it is discounted */
  originalReviewPrice?: number
  /** Controls the checkbox for the Dalton Review Product */
  isDaltonReviewChecked?: boolean
  /** Controls if helper text for payment plan appears */
  allowPaymentPlans: boolean
  /** Fires when the user checks/unchecks the Dalton Review Checkbox */
  daltonReviewChange?: (isChecked: boolean) => void
}

export const EnrollmentMainCard = (props: EnrollmentMainCardProps) => (
  <div className="rounded-md bg-white">
    <div className="sm:p-6">
      <div className="px-6 sm:px-0">
        <div className="pt-6 text-sm font-medium tracking-wider text-neutral-700 sm:p-0">
          {props.tag}
        </div>

        <div className="flex flex-wrap justify-between">
          <h2 className="mt-3 text-2xl">{props.title}</h2>

          <div className="mt-6 sm:mt-0 sm:text-end">
            <div className="text-2xl">
              <Currency dollars={props.price} />
            </div>
            {props.allowPaymentPlans && (
              <div className="mb-6 mt-3 text-sm text-neutral-700 sm:mb-0">
                Call{' '}
                <a
                  href="tel:8447217503"
                  className="font-bold"
                  suppressHydrationWarning={true}
                >
                  1-844-721-7503
                </a>{' '}
                for Payment Plan Options
              </div>
            )}
          </div>
        </div>
      </div>

      {props.checkedProChange && (
        <div className="border-backgroundShade-400 mt-12 sm:border">
          <div className="sm:p-6">
            {props.innerTags && (
              <div className="mb-6 ml-6 flex flex-wrap items-center gap-2 pl-2 text-sm font-medium tracking-wider text-neutral-700 sm:ml-9">
                {props.innerTags.map((t, i) => (
                  <Fragment key={t}>
                    {i > 0 && (
                      <div className="h-1.25 w-1.25 rounded-full bg-neutral-700" />
                    )}
                    <div>{t}</div>
                  </Fragment>
                ))}
              </div>
            )}
            <div className="mb-6 flex flex-wrap justify-between gap-6 px-6 sm:mb-0 sm:px-0">
              <Checkbox
                data-testid="dalton-review-plus-checkbox"
                checked={props.checkedPro}
                onCheckedChange={(c: boolean) =>
                  props.checkedProChange ? props.checkedProChange(c) : ''
                }
              >
                <div className="-mt-1 ml-3 cursor-pointer">
                  <div className="text-primary flex gap-2 text-2xl font-semibold">
                    <PaperAirplane aria-hidden={true} className=" mt-1" />
                    The Dalton Review® Guarantee to Pass™
                  </div>

                  <div className="max-w-[44rem] pt-3">{props.text}</div>
                </div>
              </Checkbox>

              <div>
                <div className="flex gap-4 ml-11 items-center">
                  {props.originalPlusPrice && (
                    <div className="font-normal text-base text-neutral-700 line-through">
                      <Currency dollars={props.originalPlusPrice} />
                    </div>
                  )}

                  {props.plusPrice && (
                    <div className=" font-medium sm:ml-0">
                      <Currency dollars={props.plusPrice} />
                    </div>
                  )}
                </div>

                {props.originalPlusPrice && props.plusPrice && (
                  <div className="text-end italic font-normal text-base title-font ">
                    <span className="text-periwinkle">
                      Save{' '}
                      <Currency
                        dollars={props.originalPlusPrice - props.plusPrice}
                      />
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {props.guaranteeToPass && props.checkedPro && (
            <div className="bg-grayBlue-25 mt-6 flex flex-col items-center justify-between p-6 sm:flex-row">
              <div className="flex gap-3">
                <div>
                  <div className="w-13 sm:w-[2.0625rem]">
                    <Guarantee aria-hidden={true} />
                  </div>
                </div>
                <div>
                  <div className="font-medium tracking-wider">
                    GUARANTEE TO PASS™
                  </div>
                  <div className="mt-3">
                    <div className="max-w-[33.125rem] text-sm">
                      A 100% money-back guarantee that you’ll pass the CFP®
                      exam if you successfully complete The Dalton Review.
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <GuaranteeGray aria-hidden={true} className="hidden sm:block" />
              </div>
            </div>
          )}

          {props.guaranteeToPass && !props.checkedPro && (
            <div className="mt-6 flex flex-col items-center justify-between bg-[#FFEBEB] p-6 sm:flex-row">
              <div className="ml-8 max-w-[44rem]">
                <span className="text-error-700 font-medium">
                  Don&apos;t miss out!
                </span>
                <span>
                  {' '}
                  The Dalton Review® provides additional exam prep resources
                  that increase your chances of passing, plus a 100% money-back
                  Guarantee to Pass™.
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {props.showDaltonReview && (
        <div className="border-backgroundShade-400 mt-12 sm:border">
          <div className="sm:p-6">
            <div className="mb-6 ml-6 flex flex-wrap items-center gap-2 pl-2 text-sm font-medium tracking-wider text-neutral-700 sm:ml-9">
              <div>CFP EXAM PREP</div>
            </div>

            <div className="mb-6 flex flex-wrap justify-between gap-6 px-6 sm:mb-0 sm:px-0">
              <Checkbox
                checked={props.isDaltonReviewChecked}
                data-testid="dalton-review-checkbox"
                onCheckedChange={(c: boolean) => {
                  if (props.daltonReviewChange) {
                    props.daltonReviewChange(c)
                  } else {
                    throw new Error('daltonReviewChange prop not set')
                  }
                }}
              >
                <div className=" -mt-1 ml-3 cursor-pointer">
                  <div className="text-primary flex gap-2 text-2xl font-semibold">
                    <PaperAirplane aria-hidden={true} className=" mt-1" />
                    The Dalton Review®
                  </div>

                  <div className="mb-6 max-w-[44rem] pt-3 sm:mb-0">
                    Our comprehensive review program that includes: Instructor
                    Support, Exam Prep, Study Materials
                  </div>
                </div>
              </Checkbox>

              {props.reviewPrice && (
                <div>
                  <div className="flex gap-4 ml-11 items-center">
                    {props.originalReviewPrice && (
                      <div className="font-normal text-base text-neutral-700 line-through">
                        <Currency dollars={props.originalReviewPrice} />
                      </div>
                    )}

                    {props.reviewPrice && (
                      <div className=" font-medium sm:ml-0">
                        <Currency dollars={props.reviewPrice} />
                      </div>
                    )}
                  </div>

                  {props.originalReviewPrice && props.reviewPrice && (
                    <div className="text-end italic font-normal text-base title-font ">
                      <span className="text-periwinkle">
                        Save{' '}
                        <Currency
                          dollars={
                            props.originalReviewPrice - props.reviewPrice
                          }
                        />
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {props.guaranteeToPass && !props.checkedProChange && (
        <div className="mt-6 flex flex-col items-center justify-between bg-[#F6F8FA] p-6 sm:flex-row">
          <div className="flex gap-3">
            <div>
              <div className="w-13 sm:w-[2.0625rem]">
                <Guarantee aria-hidden={true} />
              </div>
            </div>
            <div>
              <div className="font-medium tracking-wider">
                GUARANTEE TO PASS™
              </div>
              <div className="mt-3">
                <div className="max-w-[33.125rem] text-sm">
                  A 100% money-back guarantee that you’ll pass the CFP® exam if
                  you successfully complete The Dalton Review®.
                </div>
              </div>
            </div>
          </div>

          <div>
            <GuaranteeGray aria-hidden={true} className="hidden sm:block" />
          </div>
        </div>
      )}
    </div>
  </div>
)
